import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "../lib/api";
import { Header, Footer } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Check, ShieldCheck, ArrowLeft, Lock, ArrowRight, KeyRound } from "lucide-react";

const INCLUDED = [
  "Weekly Mon–Sun meal plan with 28 meals (incl. daily snack)",
  "Per-recipe kcal, protein, carbs & fat",
  "Auto-aggregated shopping list",
  "Downloadable combined PDF",
  "New menus every week",
  "Cancel anytime — self-service in Stripe",
];

export default function SubscribePage() {
  const [params] = useSearchParams();
  const cancelled = params.get("cancelled");
  const [paymentLink, setPaymentLink] = useState("");

  useEffect(() => {
    axios.get(`${API}/pricing`).then((r) => setPaymentLink(r.data?.payment_link || ""));
  }, []);

  return (
    <div className="min-h-screen flex flex-col bg-[#FAF8F2]">
      <Header />
      <main className="flex-1 py-12 sm:py-20">
        <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-10 grid gap-10 lg:grid-cols-12 lg:gap-16 items-start">
          {/* Left */}
          <div className="lg:col-span-7">
            <Link
              to="/"
              className="mb-7 inline-flex items-center text-[10px] font-semibold uppercase tracking-[0.2em] text-[#7A8880] hover:text-[#0F6B3B]"
              data-testid="back-home-link"
            >
              <ArrowLeft className="mr-2 h-3.5 w-3.5" /> Back to home
            </Link>
            <div className="flex items-center gap-3">
              <span className="h-px w-8 bg-[#0F6B3B]" />
              <span className="text-[10px] font-semibold uppercase tracking-[0.28em] text-[#0F6B3B]">
                Monthly subscription
              </span>
            </div>
            <h1 className="mt-5 font-display text-[2.6rem] sm:text-5xl lg:text-6xl font-bold leading-[1] tracking-[-0.03em] text-[#14211B]">
              €14.99
              <span className="block text-[#0F6B3B]">per month</span>
            </h1>
            <p className="mt-6 max-w-lg text-[15px] leading-relaxed text-[#56655C]">
              Full access to weekly meal plans, recipes with macros, the auto shopping list and PDF
              download. Cancel anytime.
            </p>

            {cancelled && (
              <div
                className="mt-7 border border-[#E3C98A] bg-[#FBF3DE] px-4 py-3 text-sm text-[#8A6A1F]"
                data-testid="cancelled-notice"
              >
                Checkout was cancelled. No payment was taken — try again when you're ready.
              </div>
            )}

            <div className="mt-8 border border-[#E7E3D7] bg-white p-5 sm:p-7">
              <a
                href={paymentLink || "#"}
                target={paymentLink ? "_self" : "_blank"}
                rel="noopener noreferrer"
                data-testid="subscribe-pay-link"
                aria-disabled={!paymentLink}
                onClick={(e) => {
                  if (!paymentLink) e.preventDefault();
                }}
              >
                <Button
                  type="button"
                  disabled={!paymentLink}
                  className="group h-12 w-full rounded-md bg-[#0F6B3B] text-sm font-semibold text-white hover:bg-[#0B5730]"
                  data-testid="subscribe-submit-btn"
                >
                  Continue to secure checkout
                  <ArrowRight className="ml-2.5 h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                </Button>
              </a>
              <div className="flex items-center justify-center gap-2 pt-4 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#8A968E]">
                <Lock className="h-3 w-3" /> Secure payment by Stripe
              </div>
              <p className="mt-3 text-center text-xs leading-relaxed text-[#8A968E]">
                You'll enter your card details on Stripe. After payment we'll bring you back to log in.
              </p>
            </div>

            <div className="mt-6 text-sm text-[#56655C]">
              Already paid?{" "}
              <Link
                to="/subscribe/success"
                className="font-semibold text-[#0F6B3B] hover:underline"
                data-testid="activate-access-link"
              >
                Activate access
              </Link>
            </div>
          </div>

          {/* Right */}
          <div className="lg:col-span-5 bg-[#E4EBD8] p-6 sm:p-8 lg:sticky lg:top-28">
            <div className="text-[10px] font-semibold uppercase tracking-[0.28em] text-[#0F6B3B]">
              What's included
            </div>
            <h2 className="mt-4 font-display text-2xl font-semibold leading-snug tracking-[-0.02em] text-[#14211B]">
              Everything you need to prep
            </h2>
            <ul className="mt-7 space-y-4">
              {INCLUDED.map((t) => (
                <li key={t} className="flex gap-3 text-sm leading-relaxed text-[#3F4D45]">
                  <span className="mt-0.5 h-5 w-5 flex-none rounded-full bg-[#0F6B3B] grid place-items-center">
                    <Check className="h-3 w-3 text-white" />
                  </span>
                  {t}
                </li>
              ))}
            </ul>
            <div className="mt-8 flex items-start gap-3 border-t border-[#CBD6BB] pt-6 text-sm text-[#56655C]">
              <KeyRound className="h-4 w-4 flex-none text-[#0F6B3B]" />
              <div>
                Already a coaching client? Use your weekly{" "}
                <Link to="/auth" className="underline hover:text-[#0F6B3B]">
                  4-digit code instead
                </Link>
                .
              </div>
            </div>
            <div className="mt-4 flex items-start gap-3 text-sm text-[#56655C]">
              <ShieldCheck className="h-4 w-4 flex-none text-[#0F6B3B]" />
              <div>Powered by Stripe. We never see or store your card details.</div>
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
